import React, { useEffect, useRef, useState } from 'react';
import { signOut } from 'firebase/auth';
import { doc, getDoc, onSnapshot, query, collection, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../firebase';
import OverviewView from './OverviewView';
import ChatView from './ChatView';
import CheckinView from './CheckinView';
import SettingsView from './SettingsView';
import GroupView from './GroupView';
import NotificationsView from './NotificationsView';
import CommunityView from './CommunityView';
import RasaView from './RasaView';
import ResourcesView from './ResourcesView';
import VeylSwitcher from './VeylSwitcher';
import VeylLogo from './VeylLogo';
import PushPrompt from './PushPrompt';
import { refreshPushRegistration, disablePush } from '../push';
import { useLanguage } from '../i18n';

const styleTag = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .dash-tab {
    transition: color 0.2s, border-color 0.2s, background-color 0.2s !important;
  }
  .dash-tab:hover {
    color: #00c8c8 !important;
    background-color: rgba(0,128,129,0.06) !important;
  }
  .dash-signout {
    transition: color 0.2s, background-color 0.2s !important;
  }
  .dash-signout:hover {
    color: #f87171 !important;
    background-color: rgba(248,113,113,0.08) !important;
  }
  .dash-view {
    animation: fadeIn 0.25s ease;
  }

  /* ── Responsive header ──
     Prevents the header row (logo + premium badge + name/email + sign out)
     from overflowing horizontally on narrow phone screens, which otherwise forces
     the whole page to render zoomed-out ("shrunk desktop" look). */
  @media (max-width: 480px) {
    .dash-header-inner {
      flex-wrap: wrap !important;
      row-gap: 0.5rem !important;
    }
    .dash-header-right {
      width: 100% !important;
      justify-content: space-between !important;
    }
    .dash-user-email {
      display: none !important;
    }
  }
`;

export default function DashboardPage({ user, userData, setUserData, theme, setTheme }) {
  const { t } = useLanguage();

  // t() returns the key itself when a translation is missing, so
  // "t('x') || 'fallback'" does not work. This helper gives a real
  // English fallback until the key has been added and synced.
  const tr = (key, fallback) => {
    const v = t(key);
    return !v || v === key ? fallback : v;
  };

  const [view, setView] = useState('overview');
  const [partner, setPartner] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Remembers the partner's public profile so it's fetched once per
  // partner, not on every update to the partner's veyl document.
  const partnerProfileCache = useRef({ id: null, profile: null });

  const dark = theme === 'dark';

  const themeTokens = {
    pageBg:     dark ? '#080f1a' : '#f0f4f8',
    cardBg:     dark ? 'rgba(255,255,255,0.04)' : '#FFFFFF',
    cardBorder: dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb',
    text:       dark ? '#f1f5f9' : '#1B2D44',
    subtext:    dark ? '#94a3b8' : '#6b7280',
    navBg:      dark ? 'rgba(8,15,26,0.95)' : '#FFFFFF',
    navBorder:  dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb',
    activeTab:  '#008081',
    inputBg:    dark ? 'rgba(255,255,255,0.06)' : '#FFFFFF',
  };

  const activeVeyl = userData?.activeVeyl || null;
  const activeVeylId = userData?.activeVeylId || null;

  // ── Push notifications: quietly re-link this device to the signed-in
  // user. Only does anything if notification permission was already
  // granted; it never shows a prompt. ──
  useEffect(() => {
    if (user) refreshPushRegistration();
  }, [user]);

  // Sign out: remove this device's push token first (so the next person
  // to sign in on this device doesn't get this account's alerts), but never
  // let a slow connection block the sign-out itself.
  const handleSignOut = async () => {
    try {
      await Promise.race([
        disablePush(),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);
    } catch (err) {
      console.error('Push cleanup before sign-out failed:', err);
    }
    signOut(auth);
  };

  // ── Partner fetch: partner's public profile (via Cloud Function) + veyl doc ──
  // The browser never reads the partner's user document directly — that
  // document holds their email, payment codes and strike count. The
  // getPartnerProfile function returns only name, avatar, goals achieved
  // and timezone, and only if the caller is matched with that person.
  useEffect(() => {
    const partnerUid = activeVeyl?.partnerId;
    const partnerVeylId = activeVeyl?.partnerVeylId;

    if (!partnerUid || !partnerVeylId) {
      setPartner(null);
      return;
    }

    let cancelled = false;

    const loadPartnerProfile = async () => {
      const cached = partnerProfileCache.current;
      if (cached.id === partnerUid && cached.profile) return cached.profile;

      const functions = getFunctions();
      const getPartnerProfile = httpsCallable(functions, 'getPartnerProfile');
      const result = await getPartnerProfile({ partnerId: partnerUid });
      partnerProfileCache.current = { id: partnerUid, profile: result.data };
      return result.data;
    };

    // Real-time listener on partner's veyl so goal changes + unveil reflect immediately
    const unsubVeyl = onSnapshot(
      doc(db, 'veyls', partnerVeylId),
      async (veylSnap) => {
        if (!veylSnap.exists()) return;
        const veylData = veylSnap.data();

        try {
          const profile = await loadPartnerProfile();
          if (cancelled) return;
          setPartner({
            id: partnerUid,
            ...profile,
            goalDescription: veylData.goalDescription || null,
            goalCategory:    veylData.goalCategory    || null,
            goalAchieved:    veylData.goalAchieved    || false,
            unveiled:        veylData.unveiled         || false,
            avatarId:        profile.avatarId          || null,
          });
        } catch (err) {
          console.error('Error fetching partner profile:', err);
        }
      }
    );

    return () => {
      cancelled = true;
      unsubVeyl();
    };
  }, [activeVeyl?.partnerId, activeVeyl?.partnerVeylId]);

  // ── Unread notifications badge ──
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', user.uid),
      where('read', '==', false)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      setUnreadCount(snap.size);
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (view === 'notifications') setUnreadCount(0);
  }, [view]);

  const handleSwitchVeyl = async (veylId) => {
    try {
      const veylSnap = await getDoc(doc(db, 'veyls', veylId));
      if (veylSnap.exists()) {
        const veylData = { id: veylSnap.id, ...veylSnap.data() };
        setUserData(prev => ({ ...prev, activeVeyl: veylData, activeVeylId: veylId }));
        setView('overview');
      }
    } catch (err) {
      console.error('Error switching veyl:', err);
    }
  };

  const handleNewVeyl = async (veylId) => {
    await handleSwitchVeyl(veylId);
  };

  const enrichedUserData = {
    ...userData,
    activeVeyl,
    activeVeylId,
    goalCategory:               activeVeyl?.goalCategory               || userData?.goalCategory,
    goalDescription:            activeVeyl?.goalDescription            || userData?.goalDescription,
    partnerId:                  activeVeyl?.partnerId                  || null,
    status:                     activeVeyl?.status                     || null,
    messagesUsedToday:          activeVeyl?.messagesUsedToday          || 0,
    voiceNotesUsedToday:        activeVeyl?.voiceNotesUsedToday        || 0,
    goalAchieved:               activeVeyl?.goalAchieved               || false,
    unveiled:                   activeVeyl?.unveiled                   || false,
    groupMessagesUsedToday:     userData?.groupMessagesUsedToday       || 0,
    groupVoiceNotesUsedToday:   userData?.groupVoiceNotesUsedToday     || 0,
    communityMessagesUsedToday: userData?.communityMessagesUsedToday   || 0,
  };

  const hasGroup      = !!userData?.groupId;
  const inCommunity   = !!userData?.communityId;
  const seekingPartner = enrichedUserData?.status === 'seeking_partner';

  const tabs = [
    { id: 'overview',      label: t('overview')     },
    { id: 'chat',          label: t('chat')          },
    { id: 'checkin',       label: t('checkin')       },
    { id: 'rasa', label: (t('rasa') && t('rasa') !== 'rasa') ? t('rasa') : 'Rasa' },
    { id: 'group',         label: t('group')         },
    { id: 'community',     label: t('community')     },
    { id: 'resources',     label: tr('resources', 'Resources') },
    { id: 'notifications', label: t('notifications') },
    { id: 'settings',      label: t('settings')      },
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: themeTokens.pageBg, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <style>{styleTag}</style>

      {/* ── Header ── */}
      <header style={{
        backgroundColor: themeTokens.navBg,
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${themeTokens.navBorder}`,
        padding: '0.75rem 1.5rem',
        position: 'sticky', top: 0, zIndex: 50
      }}>
        <div className="dash-header-inner" style={{ maxWidth: '72rem', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <VeylLogo width={90} />
            {userData?.isPremium && (
              <span style={{
                fontSize: '0.65rem', fontWeight: '700',
                color: '#00c8c8',
                backgroundColor: 'rgba(0,128,129,0.15)',
                border: '1px solid rgba(0,128,129,0.3)',
                padding: '0.2rem 0.6rem',
                borderRadius: '9999px',
                letterSpacing: '0.06em',
                textTransform: 'uppercase'
              }}>
                ✨ Premium
              </span>
            )}
          </div>

          <div className="dash-header-right" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: '600', color: themeTokens.text }}>
                {userData?.name}
              </div>
              <div className="dash-user-email" style={{ fontSize: '0.7rem', color: themeTokens.subtext }}>
                {userData?.email}
              </div>
            </div>
            <button
              className="dash-signout"
              onClick={handleSignOut}
              style={{
                color: themeTokens.subtext,
                background: 'none',
                border: `1px solid ${themeTokens.navBorder}`,
                borderRadius: '0.5rem',
                padding: '0.4rem 0.875rem',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.8rem'
              }}
            >
              {t('signOut')}
            </button>
          </div>
        </div>
      </header>

      {/* ── Veyl Switcher ── */}
      <VeylSwitcher
        user={user}
        userData={enrichedUserData}
        activeVeylId={activeVeylId}
        onSwitch={handleSwitchVeyl}
        onNewVeyl={handleNewVeyl}
        theme={theme}
        t={themeTokens}
      />

      {/* ── Nav tabs ── */}
      <nav style={{
        backgroundColor: themeTokens.navBg,
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${themeTokens.navBorder}`,
        position: 'sticky', top: '61px', zIndex: 40
      }}>
        <div style={{ maxWidth: '72rem', margin: '0 auto', padding: '0 1rem', display: 'flex', gap: '0.25rem', overflowX: 'auto' }}>
          {tabs.map(tab => {
            const isActive          = view === tab.id;
            const showGroupDot      = tab.id === 'group'         && !hasGroup;
            const showCommunityDot  = tab.id === 'community'     && seekingPartner && !inCommunity;
            const showNotifBadge    = tab.id === 'notifications' && unreadCount > 0;

            return (
              <button
                key={tab.id}
                className="dash-tab"
                onClick={() => setView(tab.id)}
                style={{
                  padding: '0.875rem 0.75rem',
                  color: isActive ? '#008081' : themeTokens.subtext,
                  fontWeight: isActive ? '700' : '500',
                  fontSize: '0.82rem',
                  background: 'none',
                  border: 'none',
                  borderBottom: isActive ? '2px solid #008081' : '2px solid transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  position: 'relative',
                  borderRadius: '0.5rem 0.5rem 0 0',
                }}
              >
                {tab.label}

                {(showGroupDot || showCommunityDot) && (
                  <span style={{
                    width: '6px', height: '6px', borderRadius: '50%',
                    backgroundColor: '#E05C5C',
                    display: 'inline-block', flexShrink: 0,
                    marginBottom: '6px'
                  }} />
                )}

                {showNotifBadge && (
                  <span style={{
                    backgroundColor: '#E05C5C', color: '#FFFFFF',
                    fontSize: '0.6rem', fontWeight: '700',
                    padding: '0.1rem 0.35rem', borderRadius: '999px',
                  }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* ── Main content ── */}
      <main style={{ maxWidth: '72rem', margin: '0 auto', padding: '2rem 1rem' }}>
        {(view === 'overview' || view === 'notifications') && (
          <PushPrompt t={themeTokens} showDenied={view === 'notifications'} />
        )}
        <div className="dash-view" key={view}>
          {view === 'overview' && (
            <OverviewView
              userData={enrichedUserData}
              partner={partner}
              userId={user.uid}
              user={user}
              setUserData={setUserData}
              activeVeylId={activeVeylId}
              theme={theme}
              t={themeTokens}
              translate={t}
            />
          )}
          {view === 'chat' && (
            <ChatView
              user={user}
              userData={enrichedUserData}
              partner={partner}
              setUserData={setUserData}
              activeVeylId={activeVeylId}
              theme={theme}
              t={themeTokens}
              translate={t}
            />
          )}
          {view === 'checkin' && (
            <CheckinView
              user={user}
              userData={enrichedUserData}
              partner={partner}
              activeVeylId={activeVeylId}
              theme={theme}
              t={themeTokens}
              translate={t}
            />
          )}
          {view === 'rasa' && (
            <RasaView
              user={user}
              userData={enrichedUserData}
              setUserData={setUserData}
              activeVeylId={activeVeylId}
              theme={theme}
            />
          )}
          {view === 'group' && (
            <GroupView
              user={user}
              userData={enrichedUserData}
              setUserData={setUserData}
              theme={theme}
              t={themeTokens}
              translate={t}
            />
          )}
          {view === 'community' && (
            <CommunityView
              user={user}
              userData={enrichedUserData}
              setUserData={setUserData}
              theme={theme}
              t={themeTokens}
              translate={t}
            />
          )}
          {view === 'resources' && (
            <ResourcesView
              user={user}
              userData={enrichedUserData}
              theme={theme}
              t={themeTokens}
              translate={t}
            />
          )}
          {view === 'notifications' && (
            <NotificationsView
              user={user}
              userData={enrichedUserData}
              theme={theme}
              t={themeTokens}
              translate={t}
            />
          )}
          {view === 'settings' && (
            <SettingsView
              user={user}
              userData={userData}
              setUserData={setUserData}
              theme={theme}
              setTheme={setTheme}
              t={themeTokens}
              translate={t}
            />
          )}
        </div>
      </main>
    
    </div>
  );
}